import {
  createStore,
  applyMiddleware,
  compose,
  AnyAction,
  Store as RStore,
  Unsubscribe
} from 'redux'
import subscribeActionMiddleware from 'redux-subscribe-action'
import thunk from 'redux-thunk'
import { IState } from 'types/redux'

import {
  PUBLIC_RUNTIME_CONFIG_KEY,
  COMPONENT_INITIAL_STATE_KEY
} from 'shared/store/helper/consts'

import getPreloadedState from 'shared/store/getPreloadedState'

import getReducers, { RootReducer } from 'shared/store/ducks/reducers'

import { unsubscribeAll } from './subscribe'
import { Dispatch } from 'react'
import { PublicRuntimeConfig } from 'server/shop-config/types'

type Config = {
  reducers: RootReducer
  initialState: IState
}

/**
 * singleton proxy class for the redux store
 * prevents the need of having state in the module itself which causes trouble with storybook
 * and hydrated/rendered components
 */
class Store {
  protected config: Config = null
  public store: RStore<IState, AnyAction>

  public initStore(initialState: IState | {} = {}): void {
    // we save the initial state and reducers inside the singleton so we can reset it later
    if (!this.config) {
      const preloadedState = getPreloadedState()
      const mergedState = {
        ...initialState,
        ...preloadedState
      }

      this.config = {
        initialState: mergedState,
        reducers: getReducers()
      }
    }

    this.createStore()
  }

  public createStore(): void {
    const composeEnhancers: typeof compose =
      // eslint-disable-next-line no-underscore-dangle
      global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

    this.store = createStore(
      this.config.reducers,
      this.config.initialState,
      composeEnhancers(applyMiddleware(subscribeActionMiddleware, thunk))
    )
  }

  public getStore(): RStore<IState, AnyAction> {
    return this.store
  }

  // clears the store and all subscriptions
  // this is needed for storybook to work correctly, please use with care
  public clearStore(): void {
    unsubscribeAll()
    this.store = null
  }

  // Proxy the redux store api
  public getState(): IState {
    return this.store.getState()
  }

  public dispatch(action): Dispatch<AnyAction> {
    return this.store.dispatch(action)
  }

  public subscribe(listener): Unsubscribe {
    return this.store.subscribe(listener)
  }

  public replaceReducer(nextReducer): void {
    this.store.replaceReducer(nextReducer)
  }

  // shortcut  methods
  public getPublicRuntimeConfig(): PublicRuntimeConfig {
    const state = this.getState()
    return state[PUBLIC_RUNTIME_CONFIG_KEY]
  }

  // init state
  public getComponentInitialState<T>(): T | {} {
    const state = this.getState()
    return state[COMPONENT_INITIAL_STATE_KEY] || {}
  }
}

export { subscribe } from './subscribe'

export { Store }

export default new Store()
